import { BILL_AUTOMATION_STATUS, POLLABLE_STATUSES } from 'src/stores/billAutomation/uploadedBills';

/**
 * "Use the original bill for this table" on a duplicate (LB-6016): what can be done with the bill it is a copy of.
 *
 * Only an original that is free for this table is taken, and its read becomes this table's, as if it had been uploaded
 * here; a save allocates it. Two tables of a kind split two ways already get their own use of a bill without asking
 * (LB-6016), so a service of the original already allocated is not used again: that is the same bill on two meters,
 * which one read cannot tell apart (LB-5984), or a bill entered twice, and either way a second table would get the same
 * charges.
 */
export const ORIGINAL_BILL_USE = {
  /** Read with nothing matched, one block, on no table: attached to this property, then held as this table's bill. */
  attach: 'attach',
  /** Read for this property and utility, that service on no table: held as this table's bill. */
  hold: 'hold',
  /** Not on file any more, or not one this person may read. */
  gone: 'gone',
  /** Where its services stand could not be read, so whether this table's is free is not known. */
  unchecked: 'unchecked',
  /** This table's service of it, or the bill itself, already allocated. */
  inUse: 'inUse',
  /** Still being read. */
  reading: 'reading',
  /** It could not be read, so it has nothing to fill the form with. */
  unread: 'unread',
  /** Read, and it charges nothing for this property's utility. */
  otherTable: 'otherTable',
  /**
   * Waiting for a person on the Bills page, for a choice this screen cannot make well: a portfolio bill whose sections
   * are attached one by one, or one held because its period overlaps what is billed already (LB-6094).
   */
  billsPage: 'billsPage',
  /** Anything else: a status that is never an original. */
  unusable: 'unusable'
};
/** The allocation states a service of a bill can be in and still be allocated: none yet, or a refusal to try again. */
const FREE_STATES = ['NotAllocated', 'Failed'];
/**
 * @param original    the original as bill-automation reports it, or null when it could not be read
 * @param allocations where the original's services stand (`companies/{id}/bills/{id}/allocations`), or null when that
 *                    could not be read
 * @param propertyId  the property of the table on screen
 * @param utilityId   the utility of the table on screen
 */
export function decideOriginalBillUse() {
  let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    original = _ref.original,
    allocations = _ref.allocations,
    propertyId = _ref.propertyId,
    utilityId = _ref.utilityId;
  if (!(original !== null && original !== void 0 && original.billId)) return ORIGINAL_BILL_USE.gone;
  if (!allocations) return ORIGINAL_BILL_USE.unchecked;
  if (original.status && POLLABLE_STATUSES.includes(original.status)) return ORIGINAL_BILL_USE.reading;
  switch (original.status) {
    case BILL_AUTOMATION_STATUS.needsProperty:
      {
        var _original$data, _allocations$allocati;
        // Attached here only when nothing about it was matched and it has one block: one that matched some property was
        // held for a reason a person is meant to see (a billed period overlapped), and a portfolio bill is attached
        // section by section, which the Bills page asks about and one property here would wipe.
        const recorded = [...(original.propertyIds || []), original.propertyId].filter(Boolean);
        const blocks = (_original$data = original.data) === null || _original$data === void 0 ? void 0 : _original$data.properties;
        if (recorded.length || Array.isArray(blocks) && blocks.length > 1) return ORIGINAL_BILL_USE.billsPage;
        return ((_allocations$allocati = allocations.allocationCount) !== null && _allocations$allocati !== void 0 ? _allocations$allocati : 0) > 0 ? ORIGINAL_BILL_USE.inUse : ORIGINAL_BILL_USE.attach;
      }
    case BILL_AUTOMATION_STATUS.failed:
      return ORIGINAL_BILL_USE.unread;
    case BILL_AUTOMATION_STATUS.validated:
      {
        var _service$state;
        // This table's own service of it, as its read matched it: another service of the same bill on another table
        // (an email's water and electric) leaves this one free.
        const service = (allocations.services || []).find(item => !!propertyId && !!utilityId && item.propertyId === propertyId && item.utilityId === utilityId);
        if (!service) return ORIGINAL_BILL_USE.otherTable;
        return FREE_STATES.includes((_service$state = service.state) !== null && _service$state !== void 0 ? _service$state : '') ? ORIGINAL_BILL_USE.hold : ORIGINAL_BILL_USE.inUse;
      }
    default:
      return ORIGINAL_BILL_USE.unusable;
  }
}